import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditDialog } from '../../../EditDialog';
import { EditDynamicSelectField } from '../../../EditDynamicSelectField';
import type { ContactOtherFieldsData } from '../queries/contactOthersQuery';
import { GET_CONTACT_OTHERS } from '../queries/contactOthersQuery';

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        investorType
        potentialInvestorPriority
        targetClosingDate
        investmentCommittee
        coMingledEquityFund
        coMingledCreditFundIi
        coMingled
        partnersFundIi
        equitySma
        otherSmas
        principalProtectedNote
        syndicatedDirectEquityInvestments
        gpControlInvestor
        gpNonControlInvestor
        lpCredit
        lpEquity
        lpGeneric
        imaForSbicFundCoInvesting
        imaForSecondLienLoans
        imaForSeniorLoans
      }
    }
  }
`;

const INVESTMENT_RELATED_FIELDS: Record<
  string,
  { apiField: string; isBoolean?: boolean }
> = {
  coMingled: { apiField: 'coMingled', isBoolean: true },
  coMingledCreditFundIi: { apiField: 'coMingledCreditFundIi', isBoolean: true },
  coMingledEquityFund: { apiField: 'coMingledEquityFund', isBoolean: true },
  equitySma: { apiField: 'equitySma', isBoolean: true },
  gpControlInvestor: { apiField: 'gpControlInvestor', isBoolean: true },
  gpNonControlInvestor: { apiField: 'gpNonControlInvestor', isBoolean: true },
  imaForSbicFundCoInvesting: {
    apiField: 'imaForSbicFundCoInvesting',
    isBoolean: true
  },
  imaForSecondLienLoans: {
    apiField: 'imaForSecondLienLoans',
    isBoolean: true
  },
  imaForSeniorLoans: { apiField: 'imaForSeniorLoans', isBoolean: true },
  investmentCommittee: { apiField: 'investmentCommittee', isBoolean: true },
  investorType: { apiField: 'investorType' },
  lpCredit: { apiField: 'lpCredit', isBoolean: true },
  lpEquity: { apiField: 'lpEquity', isBoolean: true },
  lpGeneric: { apiField: 'lpGeneric', isBoolean: true },
  otherSmas: { apiField: 'otherSmas', isBoolean: true },
  partnersFundIi: { apiField: 'partnersFundIi', isBoolean: true },
  potentialInvestorPriority: { apiField: 'potentialInvestorPriority' },
  principalProtectedNote: {
    apiField: 'principalProtectedNote',
    isBoolean: true
  },
  syndicatedDirectEquityInvestments: {
    apiField: 'syndicatedDirectEquityInvestments',
    isBoolean: true
  },
  targetClosingDate: { apiField: 'targetClosingDate' }
};

export interface EditInvestmentRelatedDialogProps {
  contactId: string;
  initialValue: ContactOtherFieldsData;
  isOpen: boolean;
  onChange: (data: ContactOtherFieldsData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactOtherFieldsData;
}

export function EditInvestmentRelatedDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditInvestmentRelatedDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [{ query: GET_CONTACT_OTHERS, variables: { contactId } }]
  });

  const changes = getChangedValues(value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof ContactOtherFieldsData>(
    field: K,
    fieldValue: ContactOtherFieldsData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: { id: contactId, values: changes }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save investment related');
      } else {
        toast.success('Investment related saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving investment related:', error);
      toast.error('Failed to save investment related');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Investment Related"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditDynamicSelectField
          fieldName="investor_type"
          label="Investor Type"
          model="res.partner"
          onChange={v => {
            updateField('investorType', v);
          }}
          value={value.investorType}
        />
        <EditDynamicSelectField
          fieldName="potential_investor_priority"
          label="Potential Investor Priority"
          model="res.partner"
          onChange={v => {
            updateField('potentialInvestorPriority', v);
          }}
          value={value.potentialInvestorPriority}
        />
        <EditDynamicSelectField
          fieldName="target_closing_date"
          label="Target Closing Date"
          model="res.partner"
          onChange={v => {
            updateField('targetClosingDate', v);
          }}
          value={value.targetClosingDate}
        />
        <EditCheckboxField
          checked={value.investmentCommittee}
          label="Investment Committee"
          onCheckedChange={v => {
            updateField('investmentCommittee', v);
          }}
        />
        <EditCheckboxField
          checked={value.coMingledEquityFund}
          label="Co-Mingled Equity Fund"
          onCheckedChange={v => {
            updateField('coMingledEquityFund', v);
          }}
        />
        <EditCheckboxField
          checked={value.coMingledCreditFundIi}
          label="Co-Mingled Credit Fund II"
          onCheckedChange={v => {
            updateField('coMingledCreditFundIi', v);
          }}
        />
        <EditCheckboxField
          checked={value.coMingled}
          label="Co-Mingled"
          onCheckedChange={v => {
            updateField('coMingled', v);
          }}
        />
        <EditCheckboxField
          checked={value.partnersFundIi}
          label="Partners Fund II"
          onCheckedChange={v => {
            updateField('partnersFundIi', v);
          }}
        />
        <EditCheckboxField
          checked={value.equitySma}
          label="Equity SMA"
          onCheckedChange={v => {
            updateField('equitySma', v);
          }}
        />
        <EditCheckboxField
          checked={value.otherSmas}
          label="Other SMAs"
          onCheckedChange={v => {
            updateField('otherSmas', v);
          }}
        />
        <EditCheckboxField
          checked={value.principalProtectedNote}
          label="Principal Protected Note"
          onCheckedChange={v => {
            updateField('principalProtectedNote', v);
          }}
        />
        <EditCheckboxField
          checked={value.syndicatedDirectEquityInvestments}
          label="Syndicated Direct Equity Investments"
          onCheckedChange={v => {
            updateField('syndicatedDirectEquityInvestments', v);
          }}
        />
        <EditCheckboxField
          checked={value.gpControlInvestor}
          label="GP - Control Investor"
          onCheckedChange={v => {
            updateField('gpControlInvestor', v);
          }}
        />
        <EditCheckboxField
          checked={value.gpNonControlInvestor}
          label="GP - Non Control Investor"
          onCheckedChange={v => {
            updateField('gpNonControlInvestor', v);
          }}
        />
        <EditCheckboxField
          checked={value.lpCredit}
          label="LP - Credit"
          onCheckedChange={v => {
            updateField('lpCredit', v);
          }}
        />
        <EditCheckboxField
          checked={value.lpEquity}
          label="LP - Equity"
          onCheckedChange={v => {
            updateField('lpEquity', v);
          }}
        />
        <EditCheckboxField
          checked={value.lpGeneric}
          label="LP - Generic"
          onCheckedChange={v => {
            updateField('lpGeneric', v);
          }}
        />
        <EditCheckboxField
          checked={value.imaForSbicFundCoInvesting}
          label="IMA for SBIC Fund Co-Investing"
          onCheckedChange={v => {
            updateField('imaForSbicFundCoInvesting', v);
          }}
        />
        <EditCheckboxField
          checked={value.imaForSecondLienLoans}
          label="IMA for Second Lien Loans"
          onCheckedChange={v => {
            updateField('imaForSecondLienLoans', v);
          }}
        />
        <EditCheckboxField
          checked={value.imaForSeniorLoans}
          label="IMA for Senior Loans"
          onCheckedChange={v => {
            updateField('imaForSeniorLoans', v);
          }}
        />
      </div>
    </EditDialog>
  );
}

function getChangedValues(
  current: ContactOtherFieldsData,
  initial: ContactOtherFieldsData
): Record<string, boolean | null | string> {
  const changes: Record<string, boolean | null | string> = {};

  for (const [uiField, config] of Object.entries(INVESTMENT_RELATED_FIELDS)) {
    const currentVal = current[uiField as keyof ContactOtherFieldsData];
    const initialVal = initial[uiField as keyof ContactOtherFieldsData];

    if (currentVal !== initialVal) {
      if (config.isBoolean) {
        changes[config.apiField] = currentVal as boolean;
      } else {
        const strVal = currentVal as null | string;
        changes[config.apiField] = strVal === '' ? null : (strVal ?? null);
      }
    }
  }

  return changes;
}
